import React, { useEffect, useState } from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';

import Navbar from '../../components/layout/Navbar';
import Hero from '../../components/home-sections/Hero';
import About from '../../components/home-sections/About';
import Features from '../../components/home-sections/Features';
import Streams from '../../components/home-sections/Streams';
import StorePreview from '../../components/home-sections/StorePreview';
import Footer from '../../components/layout/Footer';
import Seo from '../../components/common/Seo';

export default function Home() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [activeSection, setActiveSection] = useState(0);
  
  // 🔥 Prevent scroll from overriding active tab during click animation
  const [lockSection, setLockSection] = useState(false);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const smoothX = useSpring(mouseX, { stiffness: 50, damping: 20, mass: 0.5 });
  const smoothY = useSpring(mouseY, { stiffness: 50, damping: 20, mass: 0.5 });

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        setScrollY(window.scrollY);
        ticking = false;
      });
    };

    const handleMouseMove = (e) => {
      mouseX.set(e.clientX - 192);
      mouseY.set(e.clientY - 192);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [mouseX, mouseY]);

  useEffect(() => {
    const sections = ['hero', 'about', 'features', 'streams'];
    const observer = new IntersectionObserver(
      (entries) => {
        if (lockSection) return;
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = sections.indexOf(entry.target.id);
            if (idx !== -1) setActiveSection(idx);
          }
        });
      },
      { root: null, threshold: 0.5 }
    );

    sections.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [lockSection]);

  // 🔥 Fixed scrollToSection - forces scroll even on mobile
  const scrollToSection = (id) => {
    setLockSection(true); // Lock the active state

    // Small delay to ensure mobile menu closes first
    setTimeout(() => {
      const element = document.getElementById(id);
      
      if (element) {
        // Offset for the fixed Navbar height (approx 80px)
        const navbarHeight = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);

    // Unlock the active state after the scroll animation roughly finishes
    setTimeout(() => setLockSection(false), 1000);
  };

  return (
    // Updated bg color to #021206 to match the Borcelle aesthetic
    <div className="min-h-screen bg-[#021206] text-white relative overflow-hidden selection:bg-[#A1FF4F] selection:text-black">
      <Seo
        title="Play Hard, Vibe Harder"
        description="GameMeetsWorld is where gaming, real conversations, interviews, trends, and stream culture meet."
        path="/"
        breadcrumbs={[{ name: 'Home', path: '/' }]}
      />
      
      {/* Cursor Follower */}
      <motion.div
        className="fixed top-0 left-0 w-96 h-96 rounded-full pointer-events-none z-0 blur-[100px] opacity-20 mix-blend-screen"
        style={{ background: 'radial-gradient(circle, #A1FF4F 0%, transparent 70%)', x: smoothX, y: smoothY }}
        transition={{ type: 'spring', stiffness: 50, damping: 20, mass: 0.5 }}
      />

      <Navbar
        scrollY={scrollY}
        activeSection={activeSection}
        setActiveSection={setActiveSection}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        scrollToSection={scrollToSection}
      />

      {/* Component Stack */}
      <main className="relative z-10">
        <Hero />
        <About />    {/* Matches id="mission" */}
        <Features /> {/* Make sure Features.js has id="features" */}
        <StorePreview />
        <Streams />  {/* Matches id="streams" */}
      </main>

      <Footer />
    </div>
  );
}
